'use strict';
import { createChatBookmark } from '@api/aiAnalyzer.js';
import { getGoogleStatus, openGoogleOAuthPopup } from '@api/google.js';
import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import Tooltip from '@components/Common/Tooltip.js';
import Loader from '@components/Loading/Loader.js';
import {
  AiIcon,
  BookmarkIcon,
  CopyIcon,
  DownloadIcon,
  GoogleIcon,
} from '@icons/index.js';
import ProductsWidget from '@components/AiAnalyzer/ProductsWidget.js';
import SpatialVisionBatchResult from '@components/SpatialVision/SpatialVisionBatchResult.js';
import SpatialVisionSessionResult from '@components/SpatialVision/SpatialVisionSessionResult.js';
import { aiSelectionModeChanged } from '@signals/aiAnalyzer/index.js';
import { AiSelectedContentState } from '@states/AiAnalyzer.js';
import { SpatialVisionState } from '@states/SpatialVision.js';
import { ActiveModelState } from '@states/Viewer.js';
import { UIButton, UIDiv, UIText } from '@ui';
import { isViewerFileSource } from '@utils/cadFileModel.js';
import { marked } from 'marked';

const ICONS_STYLE =
  'size-6 flex justify-center items-center text-gray-600 hover:text-gray-800 focus:outline-none hover:scale-125 transition-transform duration-300 disabled:opacity-50';
class AiAnalyzerResult extends UIDiv {
  constructor(onDownload, onBookmarkSaved, onRetryRequest = null, sessionId) {
    super();
    this.setId('analysis-result');
    this.addClass('max-w-none space-y-4 overflow-y-auto');
    this.onDownload = onDownload;
    this.onBookmarkSaved = onBookmarkSaved;
    this.onRetryRequest = onRetryRequest;
    this.pendingRequest = null; // Store the last user request for retry
    this.sessionId = sessionId;
    this.messageIndex = 0;
    this.isSelectionMode = false;
    this.currentLinksMap = new Map(); // Store links for current streaming message
    this.currentImagesMap = new Map(); // Store images for current streaming message
    this.currentProductsMap = new Map(); // Store products for current streaming message
    this.messages = [];
    this.currentStreamingContent = ''; // Accumulate raw content during streaming
    this.isInTable = false; // Track if we're currently inside a table
    this.tableBuffer = ''; // Buffer to collect complete table content
    this.tablePlaceholderId = null; // ID of the placeholder element

    // Subscribe to selection mode changes
    this.selectionModeListener = isActive => {
      this.isSelectionMode = isActive;
      this.#updateAllSelectableElements();
    };
    aiSelectionModeChanged.add(this.selectionModeListener);

    // Subscribe to selected content state changes for visual updates
    this.unsubscribeSelectedContent = AiSelectedContentState.subscribe(() => {
      this.#updateVisualSelection();
    });
    this.unsubscribeSpatialVision = SpatialVisionState.subscribe(() => {
      if (
        this.messages.some(
          message => message.spatialVisionBatchId || message.spatialVisionJob,
        )
      ) {
        this.displayHistory(this.messages);
      }
    });
  }

  /**
   * Set the pending request that should be retried after action completion
   */
  setPendingRequest(request) {
    this.pendingRequest = request;
  }

  destroy() {
    if (this.selectionModeListener) {
      aiSelectionModeChanged.remove(this.selectionModeListener);
    }
    if (this.unsubscribeSelectedContent) {
      this.unsubscribeSelectedContent();
    }
    if (this.unsubscribeSpatialVision) {
      this.unsubscribeSpatialVision();
    }
  }

  #createMessageElement(message) {
    const messageDiv = new UIDiv();

    // Handle system messages with minimal styling
    if (message.role === 'system') {
      messageDiv.addClass(
        'px-2 py-1 text-xs text-gray-500 italic text-center bg-gray-50 rounded border-l-2 border-gray-300 mx-4 my-1',
      );
      const contentDiv = new UIDiv();
      contentDiv.setInnerHTML(message.content);
      messageDiv.add(contentDiv);
      return messageDiv;
    }

    const bgColor = message.role === 'user' ? 'bg-blue-50' : 'bg-none';
    const padding = message.role === 'user' ? 'py-4 pb-1 px-4' : 'py-1 px-2';
    messageDiv.addClass(`${bgColor} ${padding} rounded-lg overflow-auto`);

    if (message.role === 'assistant') {
      const roleDiv = new UIDiv();
      roleDiv.addClass('flex gap-2 items-start mb-2 flex-wrap');
      const roleText = new UIText(i18next.t('sidebar.aiAnalyzer.assistant'));
      roleText.addClass('text-sm font-medium');
      const roleIcon = new AiIcon({
        size: 18,
        color: 'black',
      });
      roleDiv.add(roleIcon);
      roleDiv.add(roleText);
      messageDiv.add(roleDiv);
    }

    // Check if the message contains an action (JSON with requiresAction)
    const actionData = this.#parseActionFromContent(message.content);
    if (actionData) {
      // Render action message with button
      const actionContainer = this.#createActionElement(actionData);
      messageDiv.add(actionContainer);
      return messageDiv;
    }

    const contentDiv = new UIDiv();
    contentDiv.setId(`${message.createdAt}-content`);
    contentDiv.addClass('max-w-none');
    if (message.reportContext) {
      contentDiv.dom.dataset.reportContext = JSON.stringify(
        message.reportContext,
      );
    }

    if (message.spatialVisionSession) {
      contentDiv.add(
        new SpatialVisionSessionResult({ showUserMessage: false }).render(
          message.spatialVisionSession,
        ),
      );
      messageDiv.add(contentDiv);
      return messageDiv;
    }

    if (message.spatialVisionBatchId) {
      const sessions = SpatialVisionState.spatialVision.sessions.filter(
        session => session.batchId === message.spatialVisionBatchId,
      );
      contentDiv.add(new SpatialVisionBatchResult().render(sessions));
      messageDiv.add(contentDiv);
      return messageDiv;
    }

    if (message.spatialVisionJob) {
      contentDiv.add(
        this.#renderSpatialVisionJob(
          this.#resolveSpatialVisionJob(message.spatialVisionJob),
          message.automatedTagsSources || [],
        ),
      );
      messageDiv.add(contentDiv);
      return messageDiv;
    }

    if (message.spatialVisionInput) {
      contentDiv.add(
        new UIDiv()
          .setClass('space-y-1 text-sm text-slate-700')
          .add(
            new UIText(message.content || '').setClass(
              'whitespace-pre-wrap break-words block',
            ),
            new UIText(
              [
                message.spatialVisionInput.imageName
                  ? `Image: ${message.spatialVisionInput.imageName}`
                  : null,
                message.spatialVisionInput.sequenceName
                  ? `Sequence: ${message.spatialVisionInput.sequenceName}`
                  : null,
              ]
                .filter(Boolean)
                .join(' | '),
            ).setClass('text-xs text-slate-500 block'),
          ),
      );
      messageDiv.add(contentDiv);
      return messageDiv;
    }

    // Handle streaming/loading state
    if (message.isStreaming && !message.content) {
      const loadingDiv = new UIDiv();
      loadingDiv.addClass('flex items-center gap-3 py-2');
      loadingDiv.setId('ai-loading-indicator');

      // Animated thinking dots
      const thinkingContainer = new UIDiv();
      thinkingContainer.addClass('flex items-center gap-1');

      // loading circle
      const loader = new UIDiv().setClass(
        'w-6 h-6 border-4 border-t-4 rounded-full animate-spin border-t-[#3486eb] border-[#f3f3f3]',
      );
      thinkingContainer.add(loader);

      loadingDiv.add(thinkingContainer);

      // Status text
      const statusText = new UIText(
        i18next.t('sidebar.aiAnalyzer.loading_text'),
      );
      statusText.setId('ai-status-text');
      statusText.addClass('text-sm text-gray-600 italic');
      loadingDiv.add(statusText);

      contentDiv.add(loadingDiv);
      messageDiv.add(contentDiv);
      return messageDiv;
    }

    // Check if message has products metadata and set it in the map
    if (message.products) {
      console.log('message.products', message.products);
      this.setProductsData(message.products);
    }

    // Process task references before markdown conversion
    const processedContent = this.#processContext(message.content);

    // Process links, images, and products widgets
    const processedWithWidgets =
      this.#renderMarkdownWithLinks(processedContent);

    // Use marked to convert markdown to HTML
    const htmlContent = marked.parse(processedWithWidgets, {
      gfm: true,
      breaks: true,
      headerIds: true,
      mangle: false,
    });

    contentDiv.setInnerHTML(htmlContent);

    // Render products widgets if any placeholders exist
    this.#renderProductsWidgets(contentDiv);

    // Now, for each table in the HTML content, add a copy button
    const tables = contentDiv.find('table');
    tables.forEach(table => {
      const copyButton = new UIButton();
      copyButton.addClass(`copy-table-button ${ICONS_STYLE}`);
      const copyButtonIcon = new CopyIcon({
        size: 12,
        color: 'none',
      });
      copyButton.add(copyButtonIcon);
      copyButton.onMouseEnter(() => {
        Tooltip.show(
          copyButton,
          i18next.t('sidebar.aiAnalyzer.tooltip.copy_table'),
          'top',
        );
      });
      copyButton.onMouseLeave(() => {
        Tooltip.hide();
      });
      copyButton.onClick(e => {
        e.stopPropagation();
        this.#copyTableContent(table.dom);
      });

      // Insert the copy button before the table
      const buttonContainer = new UIDiv();
      buttonContainer.addClass('flex justify-end mb-1');
      buttonContainer.add(copyButton);
      table.dom.insertAdjacentElement('beforebegin', buttonContainer.dom);
    });

    messageDiv.add(contentDiv);

    // Make elements selectable for assistant messages
    if (message.role === 'assistant') {
      this.#makeElementsSelectable(contentDiv, message, this.messageIndex);
      this.messageIndex++;
    }

    // Display credits if available and role is assistant
    if (message.role === 'assistant' && message.credits > 0) {
      const creditsDiv = new UIDiv();
      creditsDiv.addClass('text-xs text-gray-500 mt-2 text-right');
      const creditsText = new UIText(
        i18next.t('sidebar.aiAnalyzer.credits_used', {
          credits: message.credits,
        }),
      );
      creditsDiv.add(creditsText);
      messageDiv.add(creditsDiv);
    }

    // Skip action buttons for system messages
    if (message.role === 'system') {
      return messageDiv;
    }

    if (message?.hideActions) return messageDiv;
    messageDiv.add(this.#createActionButtons(message));

    return messageDiv;
  }

  #resolveSpatialVisionJob(job) {
    if (!job?.id) return job;
    return (
      SpatialVisionState.spatialVision.jobs.find(
        item => String(item.id) === String(job.id),
      ) || job
    );
  }

  #renderSpatialVisionJob(job, automatedTagsSources = []) {
    const sessions = this.#jobSessions(job, automatedTagsSources);
    if (!sessions.length) {
      return new UIDiv()
        .setClass(
          'rounded-lg border border-gray-200 bg-gray-50 px-3 py-4 text-sm text-gray-500',
        )
        .add(new UIText('No Spatial Vision job result available'));
    }

    if (sessions.length === 1) {
      return new SpatialVisionSessionResult({
        showUserMessage: false,
        showAutomatedTagsExport: true,
      }).render(sessions[0]);
    }

    return new SpatialVisionBatchResult({
      showAutomatedTagsExport: true,
    }).render(sessions);
  }

  #jobSessions(job, automatedTagsSources = []) {
    const imageResults = job?.imageResults || job?.items || [];
    return imageResults.map((item, index) => {
      const source = this.#sourceForJobItem(item, index, automatedTagsSources);
      const detections = item.detections || [];
      const resultMetadata = item.resultMetadata || job.resultMetadata || {};
      const conditionResults =
        item.conditionResults ||
        item.condition_results ||
        resultMetadata.conditionResults ||
        resultMetadata.condition_results ||
        [];
      const hasSourceContext = automatedTagsSources.length > 0;
      return {
        id: item.id || item.imageId || `${job.id}-${index}`,
        status: item.status || job.status || 'completed',
        jobId: job.id || null,
        jobItemId: item.id || item.imageId || null,
        prompt: job.prompt || item.prompt || '',
        serviceTier: job.serviceTier || 'cloud',
        inputImageUrl: item.url || item.imageUrl,
        outputImageUrl: item.outputImageUrl || item.outputImage || null,
        startedAt: job.startTime || job.startedAt || job.createdAt,
        completedAt: job.endTime || job.completedAt,
        batchId: job.id,
        batchIndex: item.itemIndex ?? index,
        batchTotal: imageResults.length,
        resultMetadata,
        llmOutput:
          resultMetadata.finalAnswer ||
          item.llmOutput ||
          item.llm_output ||
          item.summary ||
          '',
        inspectionStatus:
          item.inspectionStatus ||
          item.inspection_status ||
          resultMetadata.inspectionStatus ||
          resultMetadata.inspection_status ||
          null,
        inspectionRationale:
          item.inspectionRationale ||
          item.inspection_rationale ||
          resultMetadata.inspectionRationale ||
          resultMetadata.inspection_rationale ||
          '',
        conditionResults,
        stepResults: item.stepResults || item.step_results || [],
        canExportAutomatedTags:
          hasSourceContext &&
          (item.status || job.status) === 'completed' &&
          (detections.length > 0 || conditionResults.length > 0) &&
          !this.#isAutoTaggableSource(source),
        automatedTagsExportItem: item,
        detections: detections.map(detection =>
          this.#jobDetectionToSessionDetection(detection),
        ),
      };
    });
  }

  #sourceForJobItem(item, index, sources) {
    const itemIndex = Number(item?.itemIndex);
    if (Number.isInteger(itemIndex) && sources[itemIndex]) {
      return sources[itemIndex];
    }
    return sources[index] || null;
  }

  #isAutoTaggableSource(source) {
    return isViewerFileSource(source);
  }

  #jobDetectionToSessionDetection(detection) {
    const label =
      detection.title ||
      detection.label ||
      detection.abnormalityType ||
      detection.description ||
      'Detection';
    const coordX = detection.coordX ?? detection.bbox?.x ?? 0;
    const coordY = detection.coordY ?? detection.bbox?.y ?? 0;
    const width = detection.width ?? detection.bbox?.w ?? 0;
    const height = detection.height ?? detection.bbox?.h ?? 0;
    const colorHex = detection.colorHex || detection.color || '#2563EB';
    return {
      ...detection,
      title: label,
      description: detection.description || label,
      resultKind: detection.resultKind || 'detection',
      abnormalityType: detection.abnormalityType || label,
      coordX,
      coordY,
      width,
      height,
      area: detection.area || `${width} x ${height} px`,
      colorHex,
      inputImageUrl: detection.inputImageUrl || detection.inputImage || null,
      outputImageUrl: detection.outputImageUrl || detection.outputImage || null,
    };
  }

  #createActionButtons(message) {
    const actionButtonsDiv = new UIDiv();
    actionButtonsDiv.addClass('message-action-buttons');
    actionButtonsDiv.addClass(
      'flex justify-end items-center gap-4 min-h-[46px]',
    );

    const downloadButton = new UIButton();
    downloadButton.addClass(ICONS_STYLE);
    const downloadButtonIcon = new DownloadIcon({
      size: 18,
      color: 'none',
    });
    const downloadLoader = new Loader(
      'w-[18px] h-[18px] border-2 border-black border-t-transparent rounded-full animate-spin',
      true,
    );
    downloadLoader.hide();

    downloadButton.add(downloadButtonIcon);
    downloadButton.add(downloadLoader);
    downloadButton.onMouseEnter(() => {
      Tooltip.show(
        downloadButton,
        i18next.t('sidebar.aiAnalyzer.tooltip.download'),
        'top',
      );
    });
    downloadButton.onMouseLeave(() => {
      Tooltip.hide();
    });
    downloadButton.onClick(e => {
      e.stopPropagation();
      this.#showPdfExportOptions(message, downloadButton);
    });
    actionButtonsDiv.add(downloadButton);

    const bookmarkButton = new UIButton();
    bookmarkButton.addClass(ICONS_STYLE);
    const bookmarkButtonIcon = new BookmarkIcon({
      size: 18,
      color: 'none',
    });
    bookmarkButton.add(bookmarkButtonIcon);
    bookmarkButton.onMouseEnter(() => {
      Tooltip.show(
        bookmarkButton,
        i18next.t('sidebar.aiAnalyzer.tooltip.bookmark'),
        'top',
      );
    });
    bookmarkButton.onMouseLeave(() => {
      Tooltip.hide();
    });
    bookmarkButton.onClick(async e => {
      e.stopPropagation();
      bookmarkButton.setDisabled(true);
      await createChatBookmark({
        message: message.content,
      });
      bookmarkButton.setDisabled(false);
      toast.success(i18next.t('sidebar.aiAnalyzer.toast.bookmark_created'));
      // This will refresh the bookmark list
      this.onBookmarkSaved();
    });
    actionButtonsDiv.add(bookmarkButton);

    const copyButton = new UIButton();
    copyButton.addClass(ICONS_STYLE);
    const copyButtonIcon = new CopyIcon({
      size: 18,
      color: 'none',
    });
    copyButton.onMouseEnter(() => {
      Tooltip.show(
        copyButton,
        i18next.t('sidebar.aiAnalyzer.tooltip.copy_message'),
        'top',
      );
    });
    copyButton.onMouseLeave(() => {
      Tooltip.hide();
    });
    copyButton.add(copyButtonIcon);
    copyButton.onClick(e => {
      e.stopPropagation();
      navigator.clipboard.writeText(message.content);
      toast.success(i18next.t('sidebar.aiAnalyzer.toast.copy_message_success'));
    });
    actionButtonsDiv.add(copyButton);

    return actionButtonsDiv;
  }

  #showPdfExportOptions(message, trigger) {
    const activeModel = ActiveModelState.activeModel?.[0];
    const hasModel = Boolean(activeModel?.isObject3D);
    const exportOptions = {
      includeBlueprint: false,
      includeIsometric: false,
    };

    const content = new UIDiv();
    content.setClass('w-72 p-4 space-y-3');

    const title = new UIText(i18next.t('sidebar.aiAnalyzer.pdfExport.title'));
    title.setClass('text-sm font-semibold text-gray-900');
    content.add(title);

    const addToggle = ({ key, label, description, disabled }) => {
      const row = new UIDiv();
      row.setClass(
        [
          'flex items-start gap-3 rounded-md border border-gray-200 p-3',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        ].join(' '),
      );

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.disabled = disabled;
      checkbox.className =
        'mt-0.5 h-4 w-4 rounded border-gray-300 text-[var(--o-hive-primary-100)] focus:ring-[var(--o-hive-primary-100)]';
      checkbox.addEventListener('click', e => e.stopPropagation());
      checkbox.addEventListener('change', () => {
        exportOptions[key] = checkbox.checked;
      });

      const textStack = new UIDiv();
      textStack.setClass('flex flex-col gap-1');
      textStack.add(
        new UIText(label).setClass('text-sm font-medium text-gray-800'),
      );
      textStack.add(
        new UIText(description).setClass('text-xs leading-4 text-gray-500'),
      );

      row.dom.appendChild(checkbox);
      row.add(textStack);
      row.onClick(e => {
        e.stopPropagation();
        if (disabled) return;
        checkbox.checked = !checkbox.checked;
        exportOptions[key] = checkbox.checked;
      });
      content.add(row);
    };

    addToggle({
      key: 'includeBlueprint',
      label: i18next.t('sidebar.aiAnalyzer.pdfExport.blueprint'),
      description: hasModel
        ? i18next.t('sidebar.aiAnalyzer.pdfExport.blueprint_desc')
        : i18next.t('sidebar.aiAnalyzer.pdfExport.no_model'),
      disabled: !hasModel,
    });

    addToggle({
      key: 'includeIsometric',
      label: i18next.t('sidebar.aiAnalyzer.pdfExport.isometric'),
      description: hasModel
        ? i18next.t('sidebar.aiAnalyzer.pdfExport.isometric_desc')
        : i18next.t('sidebar.aiAnalyzer.pdfExport.no_model'),
      disabled: !hasModel,
    });

    const exportButton = new UIButton();
    exportButton.setClass(
      'w-full flex items-center justify-center gap-2 rounded-md bg-[var(--o-hive-primary-100)] px-3 py-2 text-sm font-semibold text-white hover:bg-[var(--o-hive-primary-80)] disabled:opacity-60 disabled:cursor-not-allowed',
    );
    const exportText = new UIText(
      i18next.t('sidebar.aiAnalyzer.pdfExport.export'),
    );
    const exportLoader = new Loader(
      'w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin',
      true,
    );
    exportLoader.hide();
    exportButton.add(exportText);
    exportButton.add(exportLoader);
    exportButton.onClick(async e => {
      e.stopPropagation();
      exportButton.setDisabled(true);
      exportText.setValue(i18next.t('sidebar.aiAnalyzer.pdfExport.exporting'));
      exportLoader.show();

      try {
        await this.onDownload(message, exportOptions);
        Popover.hide();
      } finally {
        exportLoader.hide();
        exportText.setValue(i18next.t('sidebar.aiAnalyzer.pdfExport.export'));
        exportButton.setDisabled(false);
      }
    });
    content.add(exportButton);

    Popover.show({
      trigger,
      content,
      placement: 'bottom-end',
      hideOnSelect: false,
      hideOnScroll: true,
      className: 'z-[10000]',
    });
  }

  addMessage(message) {
    this.messages.push(message);
    const messageElement = this.#createMessageElement(message);
    this.add(messageElement);
  }

  displayHistory(messages) {
    this.messageIndex = 0;
    this.messages = [];
    this.clear();
    messages.forEach(message => {
      this.addMessage(message);
    });
  }

  clearMessages() {
    this.messageIndex = 0;
    this.clear();
  }

  /**
   * Parse action data from message content if it's a JSON action response
   */
  #parseActionFromContent(content) {
    if (!content || typeof content !== 'string') return null;

    // Try to parse as JSON
    try {
      const trimmedContent = content.trim();
      // Check if it looks like JSON
      if (trimmedContent.startsWith('{') && trimmedContent.endsWith('}')) {
        const parsed = JSON.parse(trimmedContent);
        if (parsed.requiresAction && parsed.action) {
          return parsed;
        }
      }
    } catch (e) {
      // Not valid JSON, that's fine - it's a normal text message
    }

    return null;
  }

  /**
   * Create an action element with a button
   */
  #createActionElement(actionData) {
    const container = new UIDiv();
    container.addClass('flex flex-col items-start gap-3 py-2');

    // Message text
    const messageText = new UIText(
      actionData.action.message || 'An action is required.',
    );
    messageText.addClass('text-gray-700');
    container.add(messageText);

    // Action button
    const actionButton = new UIButton();
    actionButton.addClass(
      'px-4 py-2 bg-[var(--o-hive-primary-100)] hover:bg-[var(--o-hive-primary-80)] text-white rounded-lg font-medium transition-colors flex items-center gap-2',
    );

    // Add Google icon for google_connect action
    if (actionData.action.type === 'google_connect') {
      const googleIcon = new GoogleIcon({ size: 18 });
      actionButton.add(googleIcon);
    }

    const buttonText = new UIText(actionData.action.label || 'Take Action');
    actionButton.add(buttonText);

    // Add loading state elements
    const buttonLoader = new Loader(
      'w-[18px] h-[18px] border-2 border-white border-t-transparent rounded-full animate-spin',
      true,
    );
    buttonLoader.hide();
    actionButton.add(buttonLoader);

    // Handle button click based on action type
    actionButton.onClick(async () => {
      if (actionData.action.type === 'google_connect') {
        await this.#handleGoogleConnect(
          actionButton,
          buttonText,
          buttonLoader,
          container,
        );
      }
    });

    container.add(actionButton);

    // Check current connection status for google_connect actions
    if (actionData.action.type === 'google_connect') {
      this.#checkAndUpdateGoogleStatus(actionButton, buttonText, messageText);
    }

    return container;
  }

  /**
   * Check Google connection status and update button state
   */
  async #checkAndUpdateGoogleStatus(button, buttonText, messageText) {
    try {
      const status = await getGoogleStatus();
      if (status.connected) {
        // Update to connected state
        buttonText.setTextContent('Connected');
        messageText.setTextContent(
          `Your Google account (${status.email}) is connected.`,
        );
        button.removeClass('bg-[var(--o-hive-primary-100)]');
        button.removeClass('hover:bg-[var(--o-hive-primary-80)]');
        button.addClass('bg-green-500');
        button.addClass('cursor-default');
        button.setDisabled(true);
      }
    } catch (error) {
      // If status check fails, keep default state
      console.error('Failed to check Google status:', error);
    }
  }

  /**
   * Handle Google connect button click
   */
  async #handleGoogleConnect(button, buttonText, loader, container) {
    try {
      button.setDisabled(true);
      buttonText.setTextContent('Connecting...');
      loader.show();

      const result = await openGoogleOAuthPopup();

      if (result.success) {
        toast.success(
          `Google account connected successfully! (${result.email})`,
        );
        buttonText.setTextContent('Connected!');
        button.removeClass('bg-[var(--o-hive-primary-100)]');
        button.removeClass('hover:bg-[var(--o-hive-primary-80)]');
        button.addClass('bg-green-500');
        button.addClass('cursor-default');
        loader.hide();

        // Auto-retry the pending request if available
        if (this.pendingRequest && this.onRetryRequest) {
          // Show retrying message
          const retryingText = new UIText('Retrying your request...');
          retryingText.addClass('text-sm text-gray-500 italic mt-2');
          container.add(retryingText);

          // Small delay for UX before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Trigger retry
          this.onRetryRequest(this.pendingRequest);
          this.pendingRequest = null;
        }
      } else {
        toast.error(result.error || 'Failed to connect Google account');
        buttonText.setTextContent('Connect Google Account');
        button.setDisabled(false);
      }
    } catch (error) {
      console.error('Error connecting Google account:', error);
      toast.error('Failed to connect Google account');
      buttonText.setTextContent('Connect Google Account');
      button.setDisabled(false);
    } finally {
      loader.hide();
    }
  }

  #copyTableContent(table) {
    // Extract table content as text
    let tableContent = '';
    const rows = table.querySelectorAll('tr');
    rows.forEach(row => {
      const cells = row.querySelectorAll('td, th');
      const rowContent = Array.from(cells)
        .map(cell => cell.textContent.trim())
        .join('\t'); // Tab-separated
      tableContent += rowContent + '\n';
    });

    // Copy to clipboard
    navigator.clipboard.writeText(tableContent.trim());
    toast.success(i18next.t('sidebar.aiAnalyzer.toast.copy_table_success'));
  }

  #processContext(content) {
    const taskReferenceRegex = /\[(TASK|PROJECT):([^,]+),\s*(\d+)\]/g;

    return content.replace(taskReferenceRegex, (match, taskName, taskId) => {
      return `<span class="task-reference bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium cursor-pointer inline-block" data-task-id="${taskId}" data-task-name="${taskName.trim()}">${taskName.trim()}</span>`;
    });
  }

  #makeElementsSelectable(contentDiv, message, messageIndex) {
    // Only make product cards selectable
    const productCards = contentDiv.dom.querySelectorAll(
      '[data-product-card="true"]',
    );

    productCards.forEach((card, cardIdx) => {
      const elementId = `session-${this.sessionId}-msg-${messageIndex}-productcard-${cardIdx}`;
      card.dataset.selectableId = elementId;
      card.classList.add('selectable-element');
      this.#attachSelectionHandler(
        card,
        elementId,
        message,
        messageIndex,
        'productcard',
      );
    });
  }

  #attachSelectionHandler(
    element,
    elementId,
    message,
    messageIndex,
    elementType,
  ) {
    element.addEventListener('click', e => {
      if (!this.isSelectionMode) return;
      // Don't prevent default on button clicks inside cards
      if (e.target.closest('button')) {
        return;
      }
      e.stopPropagation();

      const selectedContent = new Map(AiSelectedContentState.selectedContent);

      if (selectedContent.has(elementId)) {
        selectedContent.delete(elementId);
      } else {
        selectedContent.set(elementId, {
          sessionId: this.sessionId,
          messageIndex,
          elementType,
          content: element.textContent.trim(),
          timestamp: message.createdAt,
          htmlContent: element.innerHTML,
        });
      }

      AiSelectedContentState.setSelectedContent(selectedContent);
    });
  }

  #updateAllSelectableElements() {
    const selectableElements = this.dom.querySelectorAll('.selectable-element');
    selectableElements.forEach(element => {
      const isProductCard = element.dataset.productCard === 'true';

      if (this.isSelectionMode) {
        element.style.cursor = 'pointer';
        element.classList.add('transition-all', 'duration-150');
      } else {
        element.style.cursor = '';
        if (isProductCard) {
          // Remove selection styles from product cards
          element.classList.remove(
            'border-2',
            'border-blue-500',
            'ring-2',
            'ring-blue-200',
          );
          // The original border border-gray-200 should still be in the base classes from ProductsWidget
        } else {
          // Remove selection styles from other elements
          element.classList.remove(
            'bg-yellow-100',
            'border-2',
            'border-yellow-400',
            'rounded',
          );
        }
      }
    });

    if (!this.isSelectionMode) {
      // Clear all selections when exiting selection mode
      AiSelectedContentState.setSelectedContent(new Map());
    }
  }

  #updateVisualSelection() {
    const selectedContent = AiSelectedContentState.selectedContent;
    const selectableElements = this.dom.querySelectorAll('.selectable-element');

    selectableElements.forEach(element => {
      const elementId = element.dataset.selectableId;
      const isProductCard = element.dataset.productCard === 'true';

      if (selectedContent.has(elementId)) {
        if (isProductCard) {
          // For product cards, use border highlighting
          element.classList.add(
            'border-2',
            'border-blue-500',
            'ring-2',
            'ring-blue-200',
          );
          // Remove the original border-gray-200 to show the blue border
          element.classList.remove('border-gray-200');
        } else {
          // For other elements (backward compatibility)
          element.classList.add(
            'bg-yellow-100',
            'border-2',
            'border-yellow-400',
            'rounded',
            'px-1',
          );
        }
      } else {
        if (isProductCard) {
          // Remove selection styles from product cards
          element.classList.remove(
            'border-2',
            'border-blue-500',
            'ring-2',
            'ring-blue-200',
          );
          // The original border border-gray-200 should still be in the base classes from ProductsWidget
        } else {
          // Remove selection styles from other elements
          element.classList.remove(
            'bg-yellow-100',
            'border-2',
            'border-yellow-400',
            'rounded',
            'px-1',
          );
        }
      }
    });
  }

  clearMessages() {
    this.messageIndex = 0;
    this.clear();
  }

  getLastMessageElement() {
    // Get the last message div element
    const messages = this.dom.querySelectorAll('[id$="-content"]');
    return messages[messages.length - 1];
  }

  updateLoadingStatus(statusText) {
    // Update the status text in the loading indicator
    const statusElement = this.dom.querySelector('#ai-status-text');
    if (statusElement) {
      statusElement.textContent = statusText;
    }
  }

  setLinksMap(links) {
    // Store links data from backend as a Map for easy lookup
    this.currentLinksMap = new Map();
    if (links && typeof links === 'object') {
      Object.entries(links).forEach(([id, urlOrData]) => {
        // Handle both string URLs and objects with url property
        const url = typeof urlOrData === 'string' ? urlOrData : urlOrData.url;
        this.currentLinksMap.set(id, { url });
      });
    }
    // Reset streaming content when new links are set (start of new message)
    this.currentStreamingContent = '';
    this.isInTable = false;
    this.tableBuffer = '';
    this.tablePlaceholderId = null;
  }

  setImagesMap(images) {
    // Store images data from backend as a Map for easy lookup
    this.currentImagesMap = new Map();
    if (images && typeof images === 'object') {
      Object.entries(images).forEach(([id, urlOrData]) => {
        const url = typeof urlOrData === 'string' ? urlOrData : urlOrData.url;
        this.currentImagesMap.set(id, { url });
      });
    }

    this.currentStreamingContent = '';
    this.isInTable = false;
    this.tableBuffer = '';
    this.tablePlaceholderId = null;
  }

  setProductsData(productsData) {
    // Store products data from backend
    this.currentProductsData = productsData;
    this.currentStreamingContent = '';
    this.isInTable = false;
    this.tableBuffer = '';
    this.tablePlaceholderId = null;
  }

  #findTableStart(lines) {
    // Find the first line that starts with |
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (trimmed.startsWith('|') && trimmed.includes('|')) {
        return i;
      }
    }
    return -1;
  }

  #findTableEnd(lines) {
    // Find where the table ends (first blank line or non-table line after table content)
    let foundTable = false;
    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (trimmed.startsWith('|')) {
        foundTable = true;
      } else if (foundTable && (trimmed === '' || !trimmed.startsWith('|'))) {
        return i - 1;
      }
    }
    return lines.length - 1;
  }

  #createTablePlaceholder() {
    return `
      <div id="${this.tablePlaceholderId}" class="my-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div class="flex items-center gap-2 text-gray-600">
          <div class="w-5 h-5 border-2 border-t-2 rounded-full animate-spin border-t-blue-500 border-gray-300"></div>
          <span class="text-sm">Loading table...</span>
        </div>
        <div class="mt-3 space-y-2">
          <div class="h-8 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-6 bg-gray-100 rounded animate-pulse"></div>
          <div class="h-6 bg-gray-100 rounded animate-pulse"></div>
          <div class="h-6 bg-gray-100 rounded animate-pulse"></div>
        </div>
      </div>
    `;
  }

  #renderCompleteTable(containerElement) {
    // Find table content in currentStreamingContent
    const lines = this.currentStreamingContent.split('\\n');
    const tableStartIndex = this.#findTableStart(lines);
    const tableEndIndex = this.#findTableEnd(lines);

    if (tableStartIndex !== -1 && tableEndIndex !== -1) {
      const tableContent = lines
        .slice(tableStartIndex, tableEndIndex + 1)
        .join('\\n');

      // Process and render the complete table
      let processedContent = this.#processContext(tableContent);
      processedContent = this.#renderMarkdownWithLinks(processedContent);
      const htmlContent = marked.parse(processedContent, {
        gfm: true,
        breaks: true,
        headerIds: true,
        mangle: false,
      });

      // Replace placeholder with rendered table
      if (this.tablePlaceholderId) {
        const placeholder = containerElement.querySelector(
          `#${this.tablePlaceholderId}`,
        );
        if (placeholder) {
          placeholder.outerHTML = htmlContent;
        } else {
          containerElement.innerHTML += htmlContent;
        }
      }
    }

    // Reset table state
    this.isInTable = false;
    this.tableBuffer = '';
    this.tablePlaceholderId = null;
  }

  #renderProductsWidgets(containerElement) {
    // Find all products widget placeholders
    const placeholders = containerElement.dom.querySelectorAll(
      '.products-widget-placeholder',
    );

    placeholders.forEach(placeholder => {
      if (this.currentProductsData) {
        // Create and render the ProductsWidget
        const productsWidget = new ProductsWidget(this.currentProductsData);
        placeholder.parentNode.replaceChild(productsWidget.dom, placeholder);
      } else {
        // Remove placeholder if no data
        placeholder.remove();
      }
    });
  }

  #renderMarkdownWithLinks(text) {
    // First handle products-widget tags
    const processedText = text.replace(/<products-widget\s*\/?>/gi, () => {
      // Find products data in the map (usually stored with query as key or 'products')
      let productsData = null;
      productsData = this.currentProductsData;

      if (productsData) {
        // Create a placeholder div that will be replaced with the actual widget
        const placeholderId = `products-widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        return `<div id="${placeholderId}" class="products-widget-placeholder"></div>`;
      }
      return ''; // Remove tag if no products data
    });

    // Then handle links looking like [Label](ID)
    // Matches patterns like [Label](FLIGHT_1), [Label](FLIGHTS_MORE), [Label](HOTEL_123), etc.
    // Pattern matches uppercase identifiers with underscores and alphanumeric characters
    return processedText.replace(
      /\[([^\]]+)\]\(([A-Z][A-Z0-9_]*)\)/g,
      (match, label, id) => {
        const realData = this.currentLinksMap.get(id);
        const imageData = this.currentImagesMap.get(id);
        if (imageData && imageData.url) {
          // Return a real HTML image tag with custom styling
          return `<img src="${imageData.url}" alt="${label}" class="inline-block w-auto h-auto max-w-full max-h-full object-contain">`;
        } else if (realData && realData.url) {
          // Return a real HTML anchor tag with custom styling
          return `<a href="${realData.url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline font-medium">${label}</a>`;
        }
        return match; // Fallback if ID not found
      },
    );
  }

  appendToStreamingMessage(content) {
    // Append content to the last message
    const lastElement = this.getLastMessageElement();
    if (lastElement) {
      // Remove loading indicator if it exists and this is the first chunk
      const loadingIndicator = lastElement.querySelector(
        '#ai-loading-indicator',
      );
      if (loadingIndicator) {
        loadingIndicator.remove();
      }

      // Accumulate raw content for final re-render
      this.currentStreamingContent += content;

      // Check for table patterns
      const lines = this.currentStreamingContent.split('\n');
      const recentLines = lines.slice(-5); // Check last 5 lines

      // Detect table start: line with | and then a separator row
      if (!this.isInTable) {
        const hasTableLine = recentLines.some(line => {
          const trimmed = line.trim();
          return trimmed.startsWith('|') && trimmed.includes('|');
        });

        const hasSeparator = recentLines.some(line => {
          const trimmed = line.trim();
          return /^\|?\s*:?-+:?\s*\|/.test(trimmed);
        });

        if (hasTableLine && hasSeparator) {
          this.isInTable = true;
          // Find where table starts in currentStreamingContent
          const tableStartIndex = this.#findTableStart(lines);
          if (tableStartIndex !== -1) {
            // Render content before table
            const beforeTable = lines.slice(0, tableStartIndex).join('\n');
            if (beforeTable.trim()) {
              let processedContent = this.#processContext(beforeTable);
              processedContent =
                this.#renderMarkdownWithLinks(processedContent);
              const htmlContent = marked.parse(processedContent, {
                gfm: true,
                breaks: true,
                headerIds: true,
                mangle: false,
              });
              lastElement.innerHTML += htmlContent;
              // Render products widgets if any
              const tempDivBefore = new UIDiv();
              tempDivBefore.dom = lastElement;
              this.#renderProductsWidgets(tempDivBefore);
            }

            // Show table placeholder
            this.tablePlaceholderId = `table-placeholder-${Date.now()}`;
            const placeholder = this.#createTablePlaceholder();
            lastElement.innerHTML += placeholder;

            // Start buffering table content
            this.tableBuffer = lines.slice(tableStartIndex).join('\n');
            return;
          }
        }
      }

      // If we're in a table, keep buffering
      if (this.isInTable) {
        // Check if table ended (blank line or non-table line after table rows)
        const lastLines = recentLines.slice(-3);
        const hasBlankLine = lastLines.some(line => line.trim() === '');
        const hasNonTableLine = lastLines.some(line => {
          const trimmed = line.trim();
          return trimmed.length > 0 && !trimmed.startsWith('|');
        });

        if (hasBlankLine || hasNonTableLine) {
          // Table ended, render it
          this.#renderCompleteTable(lastElement);

          // Continue rendering remaining content
          const tableEndIndex = this.#findTableEnd(lines);
          if (tableEndIndex !== -1 && tableEndIndex < lines.length - 1) {
            const afterTable = lines.slice(tableEndIndex + 1).join('\n');
            if (afterTable.trim()) {
              let processedContent = this.#processContext(afterTable);
              processedContent =
                this.#renderMarkdownWithLinks(processedContent);
              const htmlContent = marked.parse(processedContent, {
                gfm: true,
                breaks: true,
                headerIds: true,
                mangle: false,
              });
              lastElement.innerHTML += htmlContent;
              // Render products widgets if any
              const tempDivAfter = new UIDiv();
              tempDivAfter.dom = lastElement;
              this.#renderProductsWidgets(tempDivAfter);
            }
          }
        }
        return;
      }

      // Normal content rendering (no table)
      let processedContent = this.#processContext(content);
      processedContent = this.#renderMarkdownWithLinks(processedContent);
      const htmlContent = marked.parse(processedContent, {
        gfm: true,
        breaks: true,
        headerIds: true,
        mangle: false,
      });
      lastElement.innerHTML += htmlContent;

      // Render products widgets if any
      const tempDiv = new UIDiv();
      tempDiv.dom = lastElement;
      this.#renderProductsWidgets(tempDiv);
    }
  }

  finalizeStreamingMessage() {
    // Mark streaming as complete and add selectable elements
    const lastElement = this.getLastMessageElement();
    if (lastElement) {
      const finalContent = this.currentStreamingContent;
      const messageDiv = lastElement.closest('.bg-none');
      if (messageDiv?.dataset.streamingFinalized === 'true') return;

      // If we're still in a table when finalizing, render it now
      if (this.isInTable) {
        this.#renderCompleteTable(lastElement);
      }

      // Re-render the entire content from scratch for proper markdown formatting
      if (finalContent) {
        // Process context references
        let processedContent = this.#processContext(finalContent);

        // Replace placeholder links with real links
        processedContent = this.#renderMarkdownWithLinks(processedContent);

        // Parse markdown to HTML with full content for proper table/list rendering
        const htmlContent = marked.parse(processedContent, {
          gfm: true,
          breaks: true,
          headerIds: true,
          mangle: false,
        });

        // Replace the entire content
        lastElement.innerHTML = htmlContent;

        // Render products widgets if any
        const tempDiv = new UIDiv();
        tempDiv.dom = lastElement;
        this.#renderProductsWidgets(tempDiv);
      }

      // Reset table state
      this.isInTable = false;
      this.tableBuffer = '';
      this.tablePlaceholderId = null;

      // Clear accumulated content and links map
      this.currentStreamingContent = '';
      this.currentLinksMap.clear();
      this.currentImagesMap.clear();
      this.currentProductsMap.clear();
      // Find parent message div and mark as finalized
      if (messageDiv) {
        messageDiv.dataset.streamingFinalized = 'true';
        // Make elements selectable after streaming is complete
        const contentDiv = new UIDiv();
        contentDiv.dom = lastElement;
        this.#makeElementsSelectable(
          contentDiv,
          { role: 'assistant' },
          this.messageIndex,
        );
        this.messageIndex++;

        // Add copy buttons to tables
        const tables = lastElement.querySelectorAll('table');
        tables.forEach(table => {
          const copyButton = new UIButton();
          copyButton.addClass(`copy-table-button ${ICONS_STYLE}`);
          const copyButtonIcon = new CopyIcon({
            size: 12,
            color: 'none',
          });
          copyButton.add(copyButtonIcon);
          copyButton.onMouseEnter(() => {
            Tooltip.show(
              copyButton,
              i18next.t('sidebar.aiAnalyzer.tooltip.copy_table'),
              'top',
            );
          });
          copyButton.onMouseLeave(() => {
            Tooltip.hide();
          });
          copyButton.onClick(e => {
            e.stopPropagation();
            this.#copyTableContent(table);
          });

          // Insert the copy button before the table
          const buttonContainer = new UIDiv();
          buttonContainer.addClass('flex justify-end mb-1');
          buttonContainer.add(copyButton);
          table.insertAdjacentElement('beforebegin', buttonContainer.dom);
        });

        if (!messageDiv.querySelector('.message-action-buttons')) {
          const createdAt = lastElement.id.replace(/-content$/, '');
          let reportContext = null;
          try {
            reportContext = lastElement.dataset.reportContext
              ? JSON.parse(lastElement.dataset.reportContext)
              : null;
          } catch (error) {
            console.warn('Unable to parse report context for actions:', error);
          }
          const actionButtons = this.#createActionButtons({
            role: 'assistant',
            content: finalContent,
            createdAt,
            reportContext,
          });
          messageDiv.appendChild(actionButtons.dom);
        }
      }
    }
  }
}

export { AiAnalyzerResult };
